import React, { useState, useEffect } from 'react';
import { Shield, Settings, Check, X, Info, ChevronRight, Lock } from 'lucide-react';
import {
  getCookieConsent,
  saveCookieConsent,
  subscribeToOpenPreferences,
  applyConsentToScripts
} from '../services/cookieConsentService';

export const CookieBanner = ({ onNavigate }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [analyticsAllowed, setAnalyticsAllowed] = useState(false);
  const [marketingAllowed, setMarketingAllowed] = useState(false);

  useEffect(() => {
    // Check existing consent on mount
    const saved = getCookieConsent();
    if (!saved) {
      // Delay slightly for smooth entrance animation
      const timer = setTimeout(() => setShowBanner(true), 800);
      return () => clearTimeout(timer);
    } else {
      setAnalyticsAllowed(Boolean(saved.analytics));
      setMarketingAllowed(Boolean(saved.marketing));
      applyConsentToScripts(saved);
    }
  }, []);

  useEffect(() => {
    // Listen to global open preferences trigger (from footer or legal page)
    const unsubscribe = subscribeToOpenPreferences(() => {
      const current = getCookieConsent();
      setAnalyticsAllowed(Boolean(current?.analytics));
      setMarketingAllowed(Boolean(current?.marketing));
      setShowModal(true);
      setShowBanner(false);
    });
    return () => unsubscribe();
  }, []);

  const handleAcceptAll = () => {
    saveCookieConsent({ analytics: true, marketing: true });
    setShowBanner(false);
    setShowModal(false);
  };

  const handleRejectOptionals = () => {
    saveCookieConsent({ analytics: false, marketing: false });
    setShowBanner(false);
    setShowModal(false);
  };

  const handleSavePreferences = () => {
    saveCookieConsent({ analytics: analyticsAllowed, marketing: marketingAllowed });
    setShowModal(false);
    setShowBanner(false);
  };

  const handlePolicyLinkClick = (e) => {
    e.preventDefault();
    if (onNavigate) {
      onNavigate('cookies');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (!showBanner && !showModal) return null;

  return (
    <>
      {/* 1. BOTTOM BANNER */}
      {showBanner && !showModal && (
        <div
          role="region"
          aria-label="Consentimiento de Cookies y Privacidad"
          className="cookie-banner-container"
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '20px',
            right: '20px',
            maxWidth: '1200px',
            margin: '0 auto',
            backgroundColor: '#00244a',
            color: '#ffffff',
            borderRadius: '16px',
            padding: '20px 24px',
            boxShadow: '0 20px 50px rgba(0, 23, 48, 0.45), 0 0 0 1px rgba(243, 207, 104, 0.25)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '24px',
            flexWrap: 'wrap',
            animation: 'cookieSlideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          {/* Banner Text */}
          <div style={{ flex: '1 1 500px', minWidth: '280px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <div style={{ backgroundColor: 'rgba(243, 207, 104, 0.15)', padding: '6px', borderRadius: '8px', display: 'flex', alignItems: 'center' }}>
                <Shield size={20} color="var(--color-accent)" />
              </div>
              <h4 style={{ margin: 0, fontSize: '1.05rem', color: '#ffffff', fontFamily: 'var(--font-heading)', letterSpacing: '0.01em' }}>
                Transparencia & Privacidad en EM Asesoría
              </h4>
            </div>
            <p style={{ margin: 0, fontSize: '0.88rem', color: 'rgba(255, 255, 255, 0.86)', lineHeight: '1.55' }}>
              Utilizamos cookies técnicas necesarias para el funcionamiento del sitio web y, de manera opcional, cookies analíticas para mejorar su experiencia de búsqueda inmobiliaria en Parral. Conforme a la <strong>LFPDPPP</strong>, usted decide qué datos autoriza.{' '}
              <a
                href="#cookies"
                onClick={handlePolicyLinkClick}
                style={{ color: 'var(--color-accent)', textDecoration: 'underline', fontWeight: 600 }}
              >
                Consultar Política de Cookies
              </a>
              .
            </p>
          </div>

          {/* Banner Action Buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => {
                const current = getCookieConsent();
                setAnalyticsAllowed(Boolean(current?.analytics));
                setMarketingAllowed(Boolean(current?.marketing));
                setShowModal(true);
              }}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                padding: '9px 15px',
                fontSize: '0.85rem',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: 600
              }}
            >
              <Settings size={15} />
              <span>Configurar</span>
            </button>

            <button
              type="button"
              className="btn btn-sm"
              onClick={handleRejectOptionals}
              style={{
                backgroundColor: 'transparent',
                color: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                padding: '9px 15px',
                fontSize: '0.85rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Solo Esenciales
            </button>

            <button
              type="button"
              className="btn btn-accent btn-sm"
              onClick={handleAcceptAll}
              style={{
                padding: '9px 18px',
                fontSize: '0.85rem',
                fontWeight: 700,
                borderRadius: '8px',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(243, 207, 104, 0.35)'
              }}
            >
              Aceptar Todas
            </button>
          </div>
        </div>
      )}

      {/* 2. PREFERENCES MODAL */}
      {showModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-modal-title"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 23, 48, 0.75)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            zIndex: 10000,
            animation: 'cookieFadeIn 0.2s ease-out'
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              maxWidth: '620px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 25px 60px rgba(0, 36, 74, 0.3)',
              padding: '28px',
              border: '1px solid #e2e8f0',
              position: 'relative'
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ backgroundColor: 'rgba(0, 36, 74, 0.08)', padding: '8px', borderRadius: '10px' }}>
                  <Settings size={22} color="var(--color-primary)" />
                </div>
                <div>
                  <h3 id="cookie-modal-title" style={{ margin: 0, color: 'var(--color-primary)', fontSize: '1.25rem', fontFamily: 'var(--font-heading)' }}>
                    Centro de Preferencias de Cookies
                  </h3>
                  <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                    EM Asesoría Inmobiliaria y Jurídica · Parral, Chih.
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowModal(false)}
                aria-label="Cerrar modal de cookies"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#64748b',
                  padding: '6px',
                  borderRadius: '6px'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <p style={{ fontSize: '0.88rem', color: '#475569', lineHeight: '1.6', marginBottom: '20px' }}>
              En estricto apego a la <strong>LFPDPPP</strong> y al principio de consentimiento informado, usted puede elegir qué tecnologías de almacenamiento permite en este dispositivo.
            </p>

            {/* Category 1: Essential Cookies */}
            <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Lock size={16} color="var(--color-primary)" />
                  <strong style={{ color: 'var(--color-primary)', fontSize: '0.95rem' }}>Cookies Técnicas y Esenciales</strong>
                </div>
                <span style={{ fontSize: '0.75rem', backgroundColor: '#e2e8f0', color: '#334155', padding: '3px 8px', borderRadius: '12px', fontWeight: 700 }}>
                  Siempre Activas
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b', lineHeight: '1.5' }}>
                Garantizan la navegación segura, renderizado de mapas interactivos de Parral, persistencia del estado de sesión y almacenamiento de sus preferencias de privacidad. No almacenan datos de identificación personal.
              </p>
            </div>

            {/* Category 2: Analytics Cookies */}
            <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Info size={16} color="var(--color-accent-hover)" />
                  <strong style={{ color: 'var(--color-primary)', fontSize: '0.95rem' }}>Cookies Analíticas y de Rendimiento</strong>
                </div>
                <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={analyticsAllowed}
                    onChange={(e) => setAnalyticsAllowed(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: analyticsAllowed ? 'var(--color-primary)' : '#cbd5e1',
                      borderRadius: '24px',
                      transition: '0.2s',
                      boxShadow: analyticsAllowed ? '0 0 0 2px rgba(0, 36, 74, 0.2)' : 'none'
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        height: '18px',
                        width: '18px',
                        left: analyticsAllowed ? '23px' : '3px',
                        bottom: '3px',
                        backgroundColor: '#ffffff',
                        borderRadius: '50%',
                        transition: '0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    />
                  </span>
                </label>
              </div>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b', lineHeight: '1.5' }}>
                Nos ayudan a comprender de forma anónima cuántos usuarios consultan casas en venta en Parral o solicitan asesoría legal, permitiendo optimizar tiempos de carga y relevancia de inmuebles.
              </p>
            </div>

            {/* Category 3: Marketing / Promotional Cookies */}
            <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Shield size={16} color="var(--color-primary)" />
                  <strong style={{ color: 'var(--color-primary)', fontSize: '0.95rem' }}>Sugerencias y Personalización Inmobiliaria</strong>
                </div>
                <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={marketingAllowed}
                    onChange={(e) => setMarketingAllowed(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: marketingAllowed ? 'var(--color-primary)' : '#cbd5e1',
                      borderRadius: '24px',
                      transition: '0.2s'
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        height: '18px',
                        width: '18px',
                        left: marketingAllowed ? '23px' : '3px',
                        bottom: '3px',
                        backgroundColor: '#ffffff',
                        borderRadius: '50%',
                        transition: '0.2s'
                      }}
                    />
                  </span>
                </label>
              </div>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b', lineHeight: '1.5' }}>
                Permiten recordar sus preferencias de búsqueda (tipo de propiedad, presupuesto en MXN, colonias de Parral) para sugerirle nuevas oportunidades inmobiliarias afines.
              </p>
            </div>

            {/* Modal Actions */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={handleRejectOptionals}
                style={{ fontSize: '0.85rem' }}
              >
                Rechazar Opcionales
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleSavePreferences}
                style={{ fontSize: '0.85rem' }}
              >
                Guardar Mis Preferencias
              </button>
              <button
                type="button"
                className="btn btn-accent btn-sm"
                onClick={handleAcceptAll}
                style={{ fontSize: '0.85rem', fontWeight: 700 }}
              >
                Aceptar Todas
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
